<script setup>
// ---------------------------------------------------------------------------
// The slicers as a strip that is always there: one pill per dimension, each
// opening a panel of choices under it.
//
// The shape is the one opravujeme.to uses above its map, and it is the right
// answer for three dimensions of wildly different length — six years, three
// options, fourteen sectors. A pill says what it filters and how many are
// picked; the list is only on screen while somebody is choosing from it. The
// earlier version hid all three behind one button, which made an active filter
// easy to miss.
//
// The strip deliberately does not say how much of the dataset survived. There is
// no unit that would be true of the whole page: some charts count decisions,
// some count contracts, and one number claiming to be both would be wrong on
// every second chart. Each chart says what it is counting, under itself.
//
// State goes into the store, which mirrors it into the query string, so a
// filtered view is a link somebody can send.
// ---------------------------------------------------------------------------

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { facets, packFilter, unpackFilter } from '../cases.js'
import { filters } from '../store.js'
import { useCases } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    periodLabel: { type: String, default: '' },
    // The pill has to stay short; the panel it opens can say what it means.
    periodTitle: { type: String, default: '' },
    courtLabel: { type: String, default: '' },
    sectorLabel: { type: String, default: '' },
    // Pipe-separated: all / yes / no, in that order.
    courtOptions: { type: String, default: '' },
    // Pipe-separated: without / with, in that order.
    clearLabel: { type: String, default: '' },
    doneLabel: { type: String, default: '' }
})

const { rows } = useCases(props.src)

const open = ref(null)

const options = computed(() => (rows.value.length ? facets(rows.value) : { years: [], sectors: [] }))

const courtChoices = computed(() => {
    const labels = props.courtOptions.split('|')
    return [
        { value: '', label: labels[0] ?? '' },
        { value: 'ano', label: labels[1] ?? '' },
        { value: 'ne', label: labels[2] ?? '' }
    ]
})

const dimensions = computed(() => [
    {
        key: 'rok',
        label: props.periodLabel,
        title: props.periodTitle || props.periodLabel,
        many: true,
        values: options.value.years,
        chosen: unpackFilter(filters.rok).length
    },
    {
        key: 'soud',
        label: props.courtLabel,
        many: false,
        values: courtChoices.value,
        chosen: filters.soud ? 1 : 0
    },
    {
        key: 'obor',
        label: props.sectorLabel,
        many: true,
        values: options.value.sectors,
        chosen: unpackFilter(filters.obor).length
    }
])

const picked = (key, value) => unpackFilter(filters[key]).includes(value)

function toggle(key, value) {
    const current = unpackFilter(filters[key])

    filters[key] = packFilter(
        current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    )
}

const clear = (key) => {
    filters[key] = ''
}

// A panel closes on a click anywhere else or on Escape, the two things every
// dropdown on the web does. Choices apply as they are made — there is no
// "confirm", because the charts behind the panel are the confirmation.
const dismiss = (event) => {
    if (event.type === 'keydown' && event.key !== 'Escape') return
    open.value = null
}

onMounted(() => {
    document.addEventListener('click', dismiss)
    document.addEventListener('keydown', dismiss)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', dismiss)
    document.removeEventListener('keydown', dismiss)
})
</script>

<template>
    <div v-if="rows.length" class="filterbar" @click.stop>
        <div class="filterbar__pills">
            <div v-for="dimension in dimensions" :key="dimension.key" class="filterbar__slot">
                <button
                    type="button"
                    class="filterbar__pill"
                    :class="{ 'is-on': dimension.chosen, 'is-open': open === dimension.key }"
                    :aria-expanded="open === dimension.key"
                    @click="open = open === dimension.key ? null : dimension.key"
                >
                    {{ dimension.label }}
                    <span v-if="dimension.chosen" class="filterbar__badge">{{ dimension.chosen }}</span>
                </button>

                <div
                    v-if="open === dimension.key"
                    class="filterbar__panel"
                    :class="{ 'filterbar__panel--wide': dimension.values.length > 8 }"
                >
                    <p class="filterbar__title">{{ dimension.title ?? dimension.label }}</p>

                    <div
                        class="filterbar__options"
                        :class="{ 'filterbar__options--split': dimension.values.length > 8 }"
                    >
                        <template v-if="dimension.many">
                            <label
                                v-for="value in dimension.values"
                                :key="value"
                                class="filterbar__check"
                            >
                                <input
                                    type="checkbox"
                                    :checked="picked(dimension.key, value)"
                                    @change="toggle(dimension.key, value)"
                                >
                                <span>{{ value }}</span>
                            </label>
                        </template>

                        <template v-else>
                            <label
                                v-for="choice in dimension.values"
                                :key="choice.value"
                                class="filterbar__check"
                            >
                                <input
                                    v-model="filters[dimension.key]"
                                    type="radio"
                                    :name="dimension.key"
                                    :value="choice.value"
                                >
                                <span>{{ choice.label }}</span>
                            </label>
                        </template>
                    </div>

                    <div class="filterbar__foot">
                        <button
                            type="button"
                            class="filterbar__plain"
                            :disabled="!dimension.chosen"
                            @click="clear(dimension.key)"
                        >{{ clearLabel }}</button>

                        <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            @click="open = null"
                        >{{ doneLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
